import { ConversationTimelineDTO } from "entities/dto/ConversationTimelineDTO";
import { ConversationTimeline } from "entities/domain/conversations/timeline";
import { timelineItemFromDto } from "entities/transformers/timelineTransformer";
import { Auth0ContextInterface } from "@auth0/auth0-react/src/auth0-context";
import axios, { AxiosError } from "axios";
import ConversationDomain, {
  ConversationChannel,
} from "entities/domain/conversations/conversation-domain";
import MessageDomain from "entities/domain/conversations/message-domain";
import { ReviewProvider } from "entities/domain/reviews/review-domain";
import { CustomFields } from "entities/domain/templates";
import { ConversationDTO, MessageDTO } from "entities/dto/ConversationDTO";
import InboxDomain from "entities/domain/inbox";
import { InboxDTO } from "entities/dto/InboxDTO";
import { UnreadCountDTO } from "entities/dto/UnreadCountDTO";
import {
  conversationsTransformFromDtoToDomain,
  conversationTransformFromDtoToDomain,
} from "entities/transformers/conversation-transformer";
import { inboxTransformFromDtoToDomain } from "entities/transformers/inbox-transformer";
import {
  messagesTransformFromDtoToDomain,
  messageTransformFromDtoToDomain,
} from "entities/transformers/conversationTransformers";
import { AssignPayload, SearchField } from "redux/features/conversations";
import {
  ALL_INBOXES_SCOPE,
  InboxScope,
  inboxScopeToListParams,
} from "util/inbox-scope";
import { v5 as uuidv5 } from "uuid";
import {
  numberOfConversationsPerLoad,
  numberOfMessagesPerLoad,
} from "../util/constants";

import {
  deleteRequest,
  mutationRequest,
  patchRequest,
  postRequest,
  putRequest,
  request,
} from "../util/methods";
import { RequestType } from "./request-type";

// Fields sent to the YK CreateCRMLeads endpoint. SessionId/RooftopId are supplied
// server-side; a blank allocate_user uses the configured CRM username.
export interface YkLeadPayload {
  title: string;
  first_name: string;
  middle_name: string;
  surname: string;
  company: string;
  phone1: string;
  phone2: string;
  email: string;
  source: string;
  campaign: string;
  veh_reg: string;
  veh_desc: string;
  notes: string;
  created_date: string;
  created_time: string;
  allocate_user?: string;
}

export interface MoveConversationPayload {
  conversationId: number;
  inboxId: string;
  expectedVersion: number;
}

export const MOVE_CONVERSATION_CONFLICT_MESSAGE =
  "This conversation changed while you were moving it. Review its current details and try again.";

export class ConversationMoveConflict extends Error {
  constructor(public conversation: ConversationDomain) {
    super(MOVE_CONVERSATION_CONFLICT_MESSAGE);
  }
}

class InboxService {
  /**
   * The merchant's active Inboxes, in display order: Team Inboxes in the order
   * the Teams list returns them, then General Enquiries.
   */
  public static async getInboxes(
    { getAccessTokenSilently }: Auth0ContextInterface,
    merchantId: number
  ): Promise<InboxDomain[]> {
    const accessToken = await getAccessTokenSilently();

    const data = (
      await request<InboxDTO[]>(
        RequestType.GET,
        accessToken,
        `/merchants/${merchantId}/inboxes`
      )
    ).data;

    return data.map(inboxTransformFromDtoToDomain);
  }

  public static async getInstagramAccounts(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    { getAccessTokenSilently }: Auth0ContextInterface,
    merchantId: number
  ): Promise<
    {
      id: string;
      name: string;
    }[]
  > {
    const accessToken = await getAccessTokenSilently();

    const response = (
      await request<
        {
          id: string;
          name: string;
          logo?: string;
        }[]
      >(
        RequestType.GET,
        accessToken,
        `/merchants/${merchantId}/instagram/accounts`
      )
    ).data;

    return response;
  }

  public static async submitSelectedInstagramAccounts(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    { getAccessTokenSilently }: Auth0ContextInterface,
    selectedAccounts: string[],
    merchantId: number
  ): Promise<void> {
    const accessToken = await getAccessTokenSilently();

    const response = (
      await postRequest(
        accessToken,
        `/merchants/${merchantId}/instagram/accounts`,
        {
          account_ids: selectedAccounts,
        }
      )
    ).data;

    return response;
  }

  public static async requestReview(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    { getAccessTokenSilently }: Auth0ContextInterface,
    merchantId: number,
    conversationId: number,
    provider: ReviewProvider
  ): Promise<void> {
    const accessToken = await getAccessTokenSilently();

    await postRequest(
      accessToken,
      `/merchants/${merchantId}/conversations/${conversationId}/messages/review`,
      { provider }
    );

    return;
  }

  public static async submitSelectedFacebookPages(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    { getAccessTokenSilently }: Auth0ContextInterface,
    selectedPages: string[],
    merchantId: number
  ): Promise<void> {
    const accessToken = await getAccessTokenSilently();

    const response = (
      await postRequest(
        accessToken,
        `/merchants/${merchantId}/facebook/pages`,
        {
          page_ids: selectedPages,
        }
      )
    ).data;

    return response;
  }

  public static async getFacebookPages(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    { getAccessTokenSilently }: Auth0ContextInterface,
    merchantId: number
  ): Promise<
    {
      id: string;
      name: string;
      logo?: string;
    }[]
  > {
    const accessToken = await getAccessTokenSilently();

    const response = (
      await request<
        {
          id: string;
          name: string;
        }[]
      >(RequestType.GET, accessToken, `/merchants/${merchantId}/facebook/pages`)
    ).data;

    return response;
  }

  public static async getAutoFixedMessage(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    { getAccessTokenSilently, user }: Auth0ContextInterface,
    text: string = ""
  ): Promise<{
    text: string;
    prettified_text: string;
  }> {
    const accessToken = await getAccessTokenSilently();

    const autoFixedTextResponse = (
      await postRequest(accessToken, "/text-prettifier", { text })
    ).data;

    return autoFixedTextResponse;
  }

  public static async getAutoReplyMessage(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    { getAccessTokenSilently }: Auth0ContextInterface,
    merchantId: number,
    conversationId: number
  ): Promise<{
    conversation_id: number;
    suggested_reply: string;
  }> {
    const accessToken = await getAccessTokenSilently();

    const autoReplyMessageResponse = (
      await postRequest(
        accessToken,
        `/merchants/${merchantId}/suggested-reply`,
        {
          conversation_id: conversationId,
        }
      )
    ).data;

    return autoReplyMessageResponse;
  }

  public static async getConversations(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    { getAccessTokenSilently, user }: Auth0ContextInterface,
    status: string,
    searchText: string,
    searchField: SearchField,
    offset: number,
    merchantId: number,
    channels?: string[],
    agents?: string[],
    tagIds?: string[],
    inboxScope: InboxScope = ALL_INBOXES_SCOPE,
    sentiment?: string | null,
    urgency?: string | null,
    sortOrder?: string | null,
    signal?: AbortSignal
  ): Promise<ConversationDomain[]> {
    const accessToken = await getAccessTokenSilently();

    const queryParam: string[] = [];
    if (searchText) {
      queryParam.push(`${searchField}=${searchText}`);
    }
    if (status) {
      queryParam.push(`status=${status}`);
    }
    for (const [key, value] of Object.entries(
      inboxScopeToListParams(inboxScope)
    )) {
      queryParam.push(`${key}=${value}`);
    }

    queryParam.push(`maxPageSize=${numberOfConversationsPerLoad}`);
    queryParam.push(`offset=${offset}`);

    if (channels && channels.length > 0) {
      queryParam.push(`channels=${channels}`);
    }
    if (agents && agents.length > 0) {
      queryParam.push(`agents=${agents}`);
    }
    if (tagIds && tagIds.length > 0) {
      queryParam.push(`tag_ids=${tagIds}`);
    }
    if (sentiment) {
      queryParam.push(`sentiment=${sentiment}`);
    }
    if (urgency) {
      queryParam.push(`urgency=${urgency}`);
    }
    if (sortOrder) {
      queryParam.push(`sort_order=${sortOrder}`);
    }

    const queryString = queryParam.join("&").replace("+", "%2B");
    const url = `/merchants/${merchantId}/conversations?${queryString}`;

    // todo: use actual user id from db/auth0
    const conversationsResponse = (
      await request<ConversationDTO[]>(
        RequestType.GET,
        accessToken,
        url,
        undefined,
        signal
      )
    ).data;

    return conversationsTransformFromDtoToDomain(conversationsResponse);
  }

  public static async getConversationsByIds(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    { getAccessTokenSilently, user }: Auth0ContextInterface,
    merchantId: number,
    conversationIds: number[],
    signal?: AbortSignal
  ): Promise<ConversationDomain[]> {
    const accessToken = await getAccessTokenSilently();

    const url = `/merchants/${merchantId}/conversations?ids=${conversationIds.join(
      ","
    )}`;

    const conversationsResponse = (
      await request<ConversationDTO[]>(
        RequestType.GET,
        accessToken,
        url,
        undefined,
        signal
      )
    ).data;

    return conversationsTransformFromDtoToDomain(conversationsResponse);
  }

  public static async getPersonalConversations(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    { getAccessTokenSilently, user }: Auth0ContextInterface,
    status: string,
    searchText: string,
    searchField: SearchField,
    offset: number,
    merchantId: number,
    channels?: string[],
    tagIds?: string[],
    inboxScope: InboxScope = ALL_INBOXES_SCOPE,
    sentiment?: string | null,
    urgency?: string | null,
    sortOrder?: string | null,
    signal?: AbortSignal
  ): Promise<ConversationDomain[]> {
    const accessToken = await getAccessTokenSilently();

    const queryParam: string[] = [];
    if (searchText) {
      queryParam.push(`${searchField}=${searchText}`);
    }
    if (status) {
      queryParam.push(`status=${status}`);
    }
    for (const [key, value] of Object.entries(
      inboxScopeToListParams(inboxScope)
    )) {
      queryParam.push(`${key}=${value}`);
    }

    queryParam.push(`maxPageSize=${numberOfConversationsPerLoad}`);
    queryParam.push(`offset=${offset}`);

    if (channels && channels.length > 0) {
      queryParam.push(`channels=${channels}`);
    }
    if (tagIds && tagIds.length > 0) {
      queryParam.push(`tag_ids=${tagIds}`);
    }
    if (sentiment) {
      queryParam.push(`sentiment=${sentiment}`);
    }
    if (urgency) {
      queryParam.push(`urgency=${urgency}`);
    }
    if (sortOrder) {
      queryParam.push(`sort_order=${sortOrder}`);
    }

    const queryString = queryParam.join("&").replace("+", "%2B");
    const url = `/merchants/${merchantId}/conversations/personal?${queryString}`;

    // todo: use actual user id from db/auth0
    const conversationsResponse = (
      await request<ConversationDTO[]>(
        RequestType.GET,
        accessToken,
        url,
        undefined,
        signal
      )
    ).data;

    return conversationsTransformFromDtoToDomain(conversationsResponse);
  }

  public static async getUnassignedConversations(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    { getAccessTokenSilently, user }: Auth0ContextInterface,
    status: string,
    searchText: string,
    searchField: SearchField,
    offset: number,
    merchantId: number,
    channels?: string[],
    tagIds?: string[],
    inboxScope: InboxScope = ALL_INBOXES_SCOPE,
    sentiment?: string | null,
    urgency?: string | null,
    sortOrder?: string | null,
    signal?: AbortSignal
  ): Promise<ConversationDomain[]> {
    const accessToken = await getAccessTokenSilently();

    const queryParam: string[] = ["agents=unassigned"];
    if (searchText) {
      queryParam.push(`${searchField}=${searchText}`);
    }
    if (status) {
      queryParam.push(`status=${status}`);
    }
    for (const [key, value] of Object.entries(
      inboxScopeToListParams(inboxScope)
    )) {
      queryParam.push(`${key}=${value}`);
    }

    queryParam.push(`maxPageSize=${numberOfConversationsPerLoad}`);
    queryParam.push(`offset=${offset}`);

    if (channels && channels.length > 0) {
      queryParam.push(`channels=${channels}`);
    }
    if (tagIds && tagIds.length > 0) {
      queryParam.push(`tag_ids=${tagIds}`);
    }
    if (sentiment) {
      queryParam.push(`sentiment=${sentiment}`);
    }
    if (urgency) {
      queryParam.push(`urgency=${urgency}`);
    }
    if (sortOrder) {
      queryParam.push(`sort_order=${sortOrder}`);
    }

    const queryString = queryParam.join("&").replace("+", "%2B");
    const url = `/merchants/${merchantId}/conversations?${queryString}`;

    // todo: use actual user id from db/auth0
    const conversationsResponse = (
      await request<ConversationDTO[]>(
        RequestType.GET,
        accessToken,
        url,
        undefined,
        signal
      )
    ).data;

    return conversationsTransformFromDtoToDomain(conversationsResponse);
  }

  public static async getConversation(
    { getAccessTokenSilently }: Auth0ContextInterface,
    conversationId: number,
    merchantId: number,
    signal?: AbortSignal
  ): Promise<ConversationDomain> {
    const accessToken = await getAccessTokenSilently();
    const url = `/merchants/${merchantId}/conversations/${conversationId}`;

    const conversationsResponse = (
      await request<ConversationDTO>(
        RequestType.GET,
        accessToken,
        url,
        undefined,
        signal
      )
    ).data;

    return conversationTransformFromDtoToDomain(conversationsResponse);
  }

  public static async getUnreadCounts({
    getAccessTokenSilently,
  }: Auth0ContextInterface): Promise<UnreadCountDTO[]> {
    const accessToken = await getAccessTokenSilently();
    const url = "/merchants/conversations/unread-count";

    const unreadCountResponse = (
      await request<UnreadCountDTO[]>(RequestType.GET, accessToken, url)
    ).data;

    return unreadCountResponse;
  }

  public static async getConversationByChannelId(
    { getAccessTokenSilently }: Auth0ContextInterface,
    channelId: string,
    merchantId: number
  ): Promise<ConversationDomain | null> {
    const accessToken = await getAccessTokenSilently();
    const url = `/merchants/${merchantId}/channels/${channelId}/conversation`;

    try {
      const conversationResponse = (
        await request<ConversationDTO>(RequestType.GET, accessToken, url)
      ).data;

      return conversationTransformFromDtoToDomain(conversationResponse);
    } catch (e: unknown) {
      if (e instanceof AxiosError) {
        if (e.response!.status === 404) {
          return null;
        }
      }

      throw e;
    }
  }

  public static async getConversationTimeline(
    { getAccessTokenSilently }: Auth0ContextInterface,
    merchantId: number,
    conversationId: number,
    before?: string,
    signal?: AbortSignal
  ): Promise<ConversationTimeline> {
    const token = await getAccessTokenSilently();
    const params = new URLSearchParams({ maxPageSize: "50" });
    if (before) params.set("before", before);
    const { data } = await request<ConversationTimelineDTO>(
      RequestType.GET,
      token,
      `/merchants/${merchantId}/conversations/${conversationId}/timeline?${params}`,
      undefined,
      signal
    );
    return {
      items: data.items.map(timelineItemFromDto),
      page_info: data.page_info,
    };
  }

  public static async getMessagesInConversation(
    { getAccessTokenSilently }: Auth0ContextInterface,
    merchantId: number,
    conversationId: number,
    offset: number = 0,
    signal?: AbortSignal
  ): Promise<MessageDomain[]> {
    const accessToken = await getAccessTokenSilently();

    const messagesResponse = (
      await request<MessageDTO[]>(
        RequestType.GET,
        accessToken,
        `/merchants/${merchantId}/conversations/${conversationId}/messages?maxPageSize=${numberOfMessagesPerLoad}&offset=${offset}`,
        undefined,
        signal
      )
    ).data;

    return messagesTransformFromDtoToDomain(messagesResponse);
  }

  public static async assign(
    { getAccessTokenSilently }: Auth0ContextInterface,
    assignPayload: AssignPayload,
    merchantId: number
  ): Promise<ConversationDomain> {
    const accessToken = await getAccessTokenSilently();

    const { conversationId, agentId, aiAssistantId } = assignPayload;

    const actualPayload: { [key: string]: string | number | null } = {};

    if (typeof agentId !== "undefined") {
      actualPayload.agent_id = agentId;
    }

    if (typeof aiAssistantId !== "undefined") {
      actualPayload.ai_assistant_id = aiAssistantId;
    }

    const conversationsResponse = (
      await patchRequest<ConversationDTO>(
        RequestType.PATCH,
        accessToken,
        `/merchants/${merchantId}/conversations/${conversationId}`,
        actualPayload
      )
    ).data;

    return conversationTransformFromDtoToDomain(conversationsResponse);
  }

  public static async moveToInbox(
    { getAccessTokenSilently }: Auth0ContextInterface,
    { conversationId, inboxId, expectedVersion }: MoveConversationPayload,
    merchantId: number
  ): Promise<ConversationDomain> {
    const accessToken = await getAccessTokenSilently();
    try {
      const { data } = await patchRequest<ConversationDTO>(
        RequestType.PATCH,
        accessToken,
        `/merchants/${merchantId}/conversations/${conversationId}`,
        { inbox_id: inboxId, expected_version: expectedVersion }
      );
      return conversationTransformFromDtoToDomain(data);
    } catch (error) {
      if (
        axios.isAxiosError<{ code: string; conversation: ConversationDTO }>(
          error
        ) &&
        error.response?.status === 409 &&
        error.response.data.code === "conversation_version_conflict" &&
        error.response.data.conversation?.id === conversationId &&
        error.response.data.conversation.merchant_id === merchantId
      ) {
        throw new ConversationMoveConflict(
          conversationTransformFromDtoToDomain(error.response.data.conversation)
        );
      }
      throw error;
    }
  }

  public static async changeActiveCustomer(
    { getAccessTokenSilently }: Auth0ContextInterface,
    merchantId: number,
    conversationId: number,
    customerId: number
  ): Promise<ConversationDomain> {
    const accessToken = await getAccessTokenSilently();

    const conversationsResponse = (
      await patchRequest<ConversationDTO>(
        RequestType.PATCH,
        accessToken,
        `/merchants/${merchantId}/conversations/${conversationId}`,
        {
          customer_id: customerId,
        }
      )
    ).data;

    return conversationTransformFromDtoToDomain(conversationsResponse);
  }

  public static async takeOverConversationFromAiAssistant(
    { getAccessTokenSilently }: Auth0ContextInterface,
    merchantId: number,
    conversationId: number
  ): Promise<ConversationDomain> {
    const accessToken = await getAccessTokenSilently();

    const conversationsResponse = (
      await mutationRequest<ConversationDTO>(
        RequestType.POST,
        accessToken,
        `/merchants/${merchantId}/conversations/${conversationId}/take-over`
      )
    ).data;

    return conversationTransformFromDtoToDomain(conversationsResponse);
  }

  public static async updateConversation(
    { getAccessTokenSilently }: Auth0ContextInterface,
    assignAgentpayload: {
      conversationId: number;
      /* eslint-disable camelcase */
      is_open: boolean;
    },
    merchantId: number
  ): Promise<ConversationDomain> {
    const accessToken = await getAccessTokenSilently();

    /* eslint-disable camelcase */
    const { conversationId, is_open } = assignAgentpayload;

    const conversationsResponse = (
      await patchRequest<ConversationDTO>(
        RequestType.PATCH,
        accessToken,
        `/merchants/${merchantId}/conversations/${conversationId}`,
        {
          is_open,
        }
      )
    ).data;

    return conversationTransformFromDtoToDomain(conversationsResponse);
  }

  public static async exportConversation(
    { getAccessTokenSilently }: Auth0ContextInterface,
    conversationId: number,
    exportAddress: string,
    merchantId: number
  ): Promise<void> {
    const accessToken = await getAccessTokenSilently();

    await postRequest<ConversationDTO>(
      accessToken,
      `/merchants/${merchantId}/conversations/${conversationId}/export`,
      {
        export_address: exportAddress,
      }
    );
  }

  public static async exportToEnquiryMax(
    { getAccessTokenSilently }: Auth0ContextInterface,
    conversationId: number,
    merchantId: number
  ): Promise<void> {
    const accessToken = await getAccessTokenSilently();

    await postRequest(
      accessToken,
      `/merchants/${merchantId}/enquiry_max/leads`,
      {
        conversation_id: conversationId,
      }
    );
  }

  public static async createLeadInYK(
    { getAccessTokenSilently }: Auth0ContextInterface,
    groupId: string,
    lead: YkLeadPayload
  ): Promise<void> {
    const accessToken = await getAccessTokenSilently();

    await postRequest(
      accessToken,
      `/merchant-groups/${groupId}/yka/leads`,
      lead
    );
  }

  public static async updateMessagesReadStatus(
    { getAccessTokenSilently }: Auth0ContextInterface,
    conversationId: number,
    readThrough: string | null,
    isRead: boolean,
    merchantId: number
  ): Promise<ConversationDomain> {
    const accessToken = await getAccessTokenSilently();

    const conversationResponse = (
      await putRequest<ConversationDTO>(
        RequestType.PUT,
        accessToken,
        `/merchants/${merchantId}/conversations/${conversationId}/read-status`,
        {
          is_read: isRead,
          // The instant of the newest thread item the agent saw — a message or
          // an activity, so a missed call with no message row clears too — sent
          // back exactly as the API issued it. A JavaScript Date would drop the
          // microseconds and leave the cursor just short of the item, so the
          // conversation would stay unread on the server. Without an anchor the
          // API reads through now, or rewinds to just before the latest signal.
          read_through: readThrough ?? undefined,
        }
      )
    ).data;

    return conversationTransformFromDtoToDomain(conversationResponse);
  }

  public static async sendMessage(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    { getAccessTokenSilently, user }: Auth0ContextInterface,
    sendMessagePayLoad: {
      conversation_id: number;
      body: string;
      reply_to_message_id?: number;
      quote_reply?: boolean;
      title?: string;
      custom_fields?: CustomFields;
      template_name?: string;
      file_id?: string;
      reply_suggestion_id?: string;
    },
    merchantId: number,
    conversationId: number
  ): Promise<MessageDomain> {
    const accessToken = await getAccessTokenSilently();

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const {
      conversation_id,
      body,
      reply_to_message_id,
      quote_reply,
      title,
      custom_fields,
      template_name,
      file_id,
      reply_suggestion_id,
    } = sendMessagePayLoad;

    const headers = reply_suggestion_id
      ? {
          "Idempotency-Key": reply_suggestion_id,
        }
      : undefined;

    const message = (
      await mutationRequest(
        RequestType.POST,
        accessToken,
        `/merchants/${merchantId}/conversations/${conversationId}/messages`,
        {
          body,
          conversation_id,
          reply_to_message_id,
          quote_reply,
          title,
          custom_fields,
          template_name,
          file_ids: file_id ? [file_id] : undefined,
          reply_suggestion_id,
        },
        "application/json;charset=UTF-8",
        headers
      )
    ).data;

    return messageTransformFromDtoToDomain(message);
  }

  public static async sendTestMessage(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    { getAccessTokenSilently, user }: Auth0ContextInterface,
    sendTestMessagePayLoad: {
      handle: string;
      messageBody?: string;
      templateName?: string;
      channel: string;
    },
    merchantId: number
  ): Promise<MessageDomain> {
    const accessToken = await getAccessTokenSilently();
    // todo: use actual user id from db/auth0

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const {
      channel,
      handle,
      messageBody: body,
      templateName,
    } = sendTestMessagePayLoad;

    if (!templateName && !body) {
      throw new Error("Either templateId or body is required");
    }

    const payload: {
      handle: string;
      channel: string;
      body?: string;
      template_name?: string;
    } = {
      handle,
      channel,
    };

    if (body) {
      payload.body = body;
    }

    if (templateName) {
      payload.template_name = templateName;
    }

    const message = (
      await mutationRequest(
        RequestType.POST,
        accessToken,
        `/merchants/${merchantId}/messages/simple`,
        payload
      )
    ).data;

    return messageTransformFromDtoToDomain(message);
  }

  public static async createConversation(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    { getAccessTokenSilently, user }: Auth0ContextInterface,
    createConversationCommand: {
      channel: string;
      recipient: string;
      first_name?: string;
      last_name?: string;
      customer_id: number;
    },
    merchantId: number
  ): Promise<ConversationDomain> {
    const accessToken = await getAccessTokenSilently();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { channel, recipient, first_name, last_name, customer_id } =
      createConversationCommand;

    const conversationsResponse = (
      await mutationRequest<ConversationDTO>(
        RequestType.POST,
        accessToken,
        `/merchants/${merchantId}/conversations`,
        {
          recipient,
          channel_name: channel,
          first_name,
          last_name,
          customer_id,
        }
      )
    ).data;

    return conversationTransformFromDtoToDomain(conversationsResponse);
  }

  public static async createConversationWithChannelId(
    { getAccessTokenSilently }: Auth0ContextInterface,
    customer_channel_id: string,
    customer_id: number,
    merchantId: number
  ): Promise<ConversationDomain> {
    const accessToken = await getAccessTokenSilently();

    const conversationsResponse = (
      await mutationRequest<ConversationDTO>(
        RequestType.POST,
        accessToken,
        `/merchants/${merchantId}/conversations`,
        {
          customer_channel_id,
          customer_id,
        }
      )
    ).data;

    return conversationTransformFromDtoToDomain(conversationsResponse);
  }

  public static async addFacebookMessengerIntegration(
    { getAccessTokenSilently }: Auth0ContextInterface,
    authorization_code: string,
    redirect_uri: string,
    merchantId: number
  ) {
    const accessToken = await getAccessTokenSilently();

    await mutationRequest(
      RequestType.POST,
      accessToken,
      `/merchants/${merchantId}/facebook/authorize`,
      {
        authorization_code,
        redirect_uri,
      }
    );

    return undefined;
  }

  public static async removeFacebookMessengerIntegration(
    { getAccessTokenSilently }: Auth0ContextInterface,
    merchantId: number
  ) {
    const accessToken = await getAccessTokenSilently();

    await deleteRequest(
      RequestType.DELETE,
      accessToken,
      `/merchants/${merchantId}/facebook/authorize`
    );

    return undefined;
  }

  public static async addInstagramIntegration(
    { getAccessTokenSilently }: Auth0ContextInterface,
    authorization_code: string,
    redirect_uri: string,
    merchantId: number
  ) {
    const accessToken = await getAccessTokenSilently();

    await mutationRequest(
      RequestType.POST,
      accessToken,
      `/merchants/${merchantId}/instagram/authorize`,
      {
        authorization_code,
        redirect_uri,
      }
    );

    return undefined;
  }

  public static async removeInstagramIntegration(
    { getAccessTokenSilently }: Auth0ContextInterface,
    merchantId: number
  ) {
    const accessToken = await getAccessTokenSilently();

    await deleteRequest(
      RequestType.DELETE,
      accessToken,
      `/merchants/${merchantId}/instagram/authorize`
    );

    return undefined;
  }

  public static async bulkOpenOrCloseConversations(
    { getAccessTokenSilently }: Auth0ContextInterface,
    merchantId: number,
    conversationIds: number[],
    shouldOpen: boolean
  ): Promise<number[]> {
    const accessToken = await getAccessTokenSilently();

    const response = (
      await patchRequest<{ updated_conversations: number[] }>(
        RequestType.PATCH,
        accessToken,
        `/merchants/${merchantId}/conversations/bulk/${
          shouldOpen ? "open" : "close"
        }`,
        {
          conversation_ids: conversationIds,
        }
      )
    ).data;

    return response.updated_conversations;
  }

  public static async bulkMarkReadOrUnread(
    { getAccessTokenSilently }: Auth0ContextInterface,
    merchantId: number,
    conversationIds: number[],
    shouldMarkAsRead: boolean
  ): Promise<number[]> {
    const accessToken = await getAccessTokenSilently();

    const response = (
      await patchRequest<{ updated_conversations: number[] }>(
        RequestType.PATCH,
        accessToken,
        `/merchants/${merchantId}/conversations/bulk/${
          shouldMarkAsRead ? "read" : "unread"
        }`,
        {
          conversation_ids: conversationIds,
        }
      )
    ).data;

    return response.updated_conversations;
  }

  public static async bulkAssignConversations(
    { getAccessTokenSilently }: Auth0ContextInterface,
    merchantId: number,
    conversationIds: number[],
    agentId?: number | null,
    inboxId?: string
  ): Promise<number[]> {
    const accessToken = await getAccessTokenSilently();

    const payload: { [key: string]: string | number | number[] | null } = {
      conversation_ids: conversationIds,
    };

    if (typeof agentId !== "undefined") {
      payload.agent_id = agentId;
    }

    if (typeof inboxId !== "undefined") {
      payload.inbox_id = inboxId;
    }

    const response = (
      await patchRequest<{ updated_conversations: number[] }>(
        RequestType.PATCH,
        accessToken,
        `/merchants/${merchantId}/conversations/bulk/assign`,
        payload
      )
    ).data;

    return response.updated_conversations;
  }

  public static async getAiConversationHistoryBoundary(
    { getAccessTokenSilently }: Auth0ContextInterface,
    merchantId: number,
    conversationId: number,
    signal?: AbortSignal
  ): Promise<{
    conversation_id: number;
    boundary_timestamp: string;
  } | null> {
    const accessToken = await getAccessTokenSilently();
    const response = await request<{
      conversation_id: number;
      boundary_timestamp: string;
    }>(
      RequestType.GET,
      accessToken,
      `/merchants/${merchantId}/ai-conversation-history-boundaries/${conversationId}`,
      undefined,
      signal
    );

    return response.data;
  }

  public static async setAiConversationHistoryBoundary(
    { getAccessTokenSilently }: Auth0ContextInterface,
    merchantId: number,
    conversationId: number,
    boundaryTimestamp: string
  ): Promise<{
    conversation_id: number;
    boundary_timestamp: string;
  }> {
    const accessToken = await getAccessTokenSilently();
    const response = await putRequest<{
      conversation_id: number;
      boundary_timestamp: string;
    }>(
      RequestType.PUT,
      accessToken,
      `/merchants/${merchantId}/ai-conversation-history-boundaries/${conversationId}`,
      {
        boundary_timestamp: boundaryTimestamp,
      }
    );

    return response.data;
  }

  public static async removeAiConversationHistoryBoundary(
    { getAccessTokenSilently }: Auth0ContextInterface,
    merchantId: number,
    conversationId: number
  ): Promise<void> {
    const accessToken = await getAccessTokenSilently();

    await deleteRequest(
      RequestType.DELETE,
      accessToken,
      `/merchants/${merchantId}/ai-conversation-history-boundaries/${conversationId}`
    );
  }
}

export default InboxService;
